/**
 * FinanceSection Component
 * 價格與財務計算區塊
 * Requirements: 5.1, 5.2, 5.3, 5.4
 */

import type { CardData } from '../../types/card';
import {
  currencyOptions,
  targetCurrencyOptions,
} from '../../types/card';
import type { Translations } from '../../i18n';

interface FinanceSectionProps {
  currency: CardData['currency'];
  renewalPrice: number;
  sellingPrice: number;
  billingCycle: number;
  purchaseDate: string;
  nextDueDate: string;
  exchangeRate: number;
  targetCurrency: CardData['targetCurrency'];
  onCurrencyChange: (value: CardData['currency']) => void;
  onRenewalPriceChange: (value: number) => void;
  onSellingPriceChange: (value: number) => void;
  onBillingCycleChange: (value: number) => void;
  onPurchaseDateChange: (value: string) => void;
  onNextDueDateChange: (value: string) => void;
  onExchangeRateChange: (value: number) => void;
  onTargetCurrencyChange: (value: CardData['targetCurrency']) => void;
  t: Translations;
}

export function FinanceSection({
  currency,
  renewalPrice,
  sellingPrice,
  billingCycle,
  purchaseDate,
  nextDueDate,
  exchangeRate,
  targetCurrency,
  onCurrencyChange,
  onRenewalPriceChange,
  onSellingPriceChange,
  onBillingCycleChange,
  onPurchaseDateChange,
  onNextDueDateChange,
  onExchangeRateChange,
  onTargetCurrencyChange,
  t,
}: FinanceSectionProps) {
  const isLifetime = billingCycle === 0;

  const billingCycleOptions = [
    { value: 30, label: `${t.monthly} (30${t.days})` },
    { value: 365, label: t.yearly },
    { value: 730, label: t.twoYears },
    { value: 0, label: t.lifetime },
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">{t.priceInfo}</h3>

      {/* 續費價格和幣種 */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.renewalPrice}</label>
          <input
            type="number"
            value={renewalPrice}
            onChange={(e) => onRenewalPriceChange(parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.currency}</label>
          <select
            value={currency}
            onChange={(e) => onCurrencyChange(e.target.value as CardData['currency'])}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {currencyOptions.map((cur) => (
              <option key={cur} value={cur}>{cur}</option>
            ))}
          </select>
        </div>
      </div>

      {/* 計費週期 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t.billingCycle}</label>
        <select
          value={billingCycle}
          onChange={(e) => onBillingCycleChange(parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {billingCycleOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {/* 購買日期和到期日 */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.purchaseDate}</label>
          <input
            type="date"
            value={purchaseDate}
            onChange={(e) => onPurchaseDateChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t.dueDate} {isLifetime && <span className="text-green-600">({t.lifetimeValid})</span>}
          </label>
          <input
            type="date"
            value={nextDueDate}
            onChange={(e) => onNextDueDateChange(e.target.value)}
            disabled={isLifetime}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isLifetime ? 'bg-gray-100 cursor-not-allowed' : ''
            }`}
          />
        </div>
      </div>

      {/* 預期售價 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">{t.sellingPrice}</label>
        <input
          type="number"
          value={sellingPrice}
          onChange={(e) => onSellingPriceChange(parseFloat(e.target.value) || 0)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          min="0"
          step="0.01"
        />
      </div>

      {/* 匯率和目標幣種 */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.exchangeRate}</label>
          <input
            type="number"
            value={exchangeRate}
            onChange={(e) => onExchangeRateChange(parseFloat(e.target.value) || 0)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t.targetCurrency}</label>
          <select
            value={targetCurrency}
            onChange={(e) => onTargetCurrencyChange(e.target.value as CardData['targetCurrency'])}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {targetCurrencyOptions.map((cur) => (
              <option key={cur} value={cur}>{cur}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
